import React, { useEffect, useRef, useState } from 'react';
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TimelineChartProps } from '../../../types/reports';
import { findPercentile, getNasemScoreConfig, getValueForPercentile } from '../../../utils/reports';
import { ResultTimelineNasemTooltip } from '../elements/ResultTimelineNasemTooltip';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

interface CustomDoubleLabelProps {
  viewBox: {
    x: number;
    y: number;
    width: number;
  };
  valueLeft: string;
  valueRight: string;
}

const CHART_HEIGHT = 400;

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const lineData = payload.find((item: { dataKey: string }) => item.dataKey === 'userPercent');

    if (lineData) {
      const data = lineData.payload;
      return <ResultTimelineNasemTooltip reportData={data} />;
    }
  }
  return null;
};

const NasemScoreTimelineChart: React.FC<TimelineChartProps> = ({ graphData, allResults }) => {
  const { theme } = useThemeContext();

  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        setChartWidth(chartRef.current.offsetWidth || 0);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const CustomDoubleLabel = ({ viewBox, valueLeft, valueRight }: CustomDoubleLabelProps) => {
    const { x, y, width } = viewBox;
    return (
      <>
        <text
          x={x + 15}
          y={y + 4}
          textAnchor="end"
          style={{
            fill: theme.custom.report.timeline.nasemScoreTimelineChart.customDoubleLabel.left.color,
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          {valueLeft}
        </text>
        <text
          x={x + width - 50}
          y={y + 4}
          textAnchor="start"
          style={{
            fill: theme.custom.report.timeline.nasemScoreTimelineChart.customDoubleLabel.right.color,
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          {valueRight}
        </text>
      </>
    );
  };

  let chartData = allResults.map((report) => ({
    date: new Date(report.report_date).toLocaleDateString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    }),
    low: findPercentile(2.0, graphData),
    medium: findPercentile(20.0, graphData) - findPercentile(2.0, graphData),
    high: 100 - findPercentile(20.0, graphData),
    userPercent: findPercentile(report.nasemScore, graphData),
    totalCompoundsDetected: report.totalCompoundsDetected,
    totalPFASScore: report.totalPFASScore,
    nasemLevel: report.nasemLevel,
    nasemScore: report.nasemScore,
  }));

  const referenceLines = [
    { y: findPercentile(2.0, graphData), leftLabel: `${findPercentile(2.0, graphData)}%`, rightLabel: '2 ng/mL' },
    { y: 50, leftLabel: '50%', rightLabel: `${getValueForPercentile(50, graphData)} ng/mL` },
    { y: findPercentile(20.0, graphData), leftLabel: `${findPercentile(20.0, graphData)}%`, rightLabel: '20 ng/mL' },
    { y: 100, leftLabel: '100%', rightLabel: `${getValueForPercentile(100, graphData)} ng/mL` },
  ];

  return (
    <div
      ref={chartRef}
      style={{
        position: 'relative',
        width: '100%',
        height: CHART_HEIGHT,
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: 20, right: 50, left: 30, bottom: 20 }}>
          {/* to control the areas without padding  */}
          <XAxis dataKey="date" xAxisId="areas" hide padding={{ left: 30, right: 60 }} />
          {/* to control lines with dots with more padding  */}
          <XAxis
            xAxisId="main"
            dataKey="date"
            tick={{
              fontSize: 12,
              fill: theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.xAxis.tick.color,
              fontWeight: 400,
            }}
            axisLine={{
              stroke: theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.xAxis.axisLine.color,
            }}
            tickLine={{
              stroke: theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.xAxis.tickLine.color,
            }}
            padding={{ left: 80, right: 90 }}
          />
          <YAxis
            hide
            domain={[0, 100]}
            tick={{
              fontSize: 12,
              fill: theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.yAxis.tick.color,
            }}
            axisLine={{
              stroke: theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.yAxis.axisLine.color,
            }}
            tickLine={{
              stroke: theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.yAxis.tickLine.color,
            }}
          />
          <Tooltip
            cursor={false}
            content={({ active, payload }) => <CustomTooltip active={active} payload={payload} />}
          />

          {referenceLines.map((line, index) => (
            <ReferenceLine
              xAxisId="main"
              key={index}
              y={line.y}
              stroke={theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.referenceLines.stroke.color}
              strokeWidth={2}
              label={
                <CustomDoubleLabel
                  viewBox={{
                    x: -20,
                    y: (CHART_HEIGHT * (100 - line.y)) / 100,
                    width: chartWidth,
                  }}
                  valueLeft={line.leftLabel}
                  valueRight={line.rightLabel}
                />
              }
              ifOverflow="visible"
              clipPath="inset(0px 60px 0px 30px)"
            />
          ))}

          <Area
            xAxisId="areas"
            type="monotone"
            baseValue={0}
            dataKey="low"
            stackId="1"
            fill={theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.area.low.color}
            fillOpacity={1}
            stroke="none"
            activeDot={false}
          />
          <Area
            xAxisId="areas"
            type="monotone"
            baseValue={0}
            dataKey="medium"
            stackId="1"
            fill={theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.area.medium.color}
            fillOpacity={1}
            stroke="none"
            activeDot={false}
          />
          <Area
            xAxisId="areas"
            type="monotone"
            baseValue={0}
            dataKey="high"
            stackId="1"
            fill={theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.area.high.color}
            fillOpacity={1}
            stroke="none"
            activeDot={false}
          />

          <Line
            xAxisId="main"
            type="linear"
            dataKey="userPercent"
            stroke="none"
            activeDot={({ cx, cy, payload }: any) => {
              const { exposureTextColor, badgeBackgroundColor } = getNasemScoreConfig(payload.nasemScore, theme);
              return (
                <circle cx={cx} cy={cy} r={8} fill={badgeBackgroundColor} stroke={exposureTextColor} strokeWidth={3} />
              );
            }}
            dot={({ cx, cy, payload }) => {
              const { exposureTextColor, badgeBackgroundColor } = getNasemScoreConfig(payload.nasemScore, theme);
              return (
                <>
                  <line
                    x1={cx}
                    y1={cy}
                    x2={cx}
                    y2={CHART_HEIGHT - 40}
                    stroke={theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.line.dot.stroke.color}
                  />
                  <circle
                    cx={cx}
                    cy={cy}
                    r={6}
                    fill={badgeBackgroundColor}
                    stroke={exposureTextColor}
                    strokeWidth={1}
                  />
                </>
              );
            }}
          />
          <ReferenceLine
            xAxisId="main"
            y={0.0}
            stroke={theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.referenceLines.y0.stroke.color}
            strokeWidth={2}
            label={{
              value: '',
              position: 'left',
              fill: theme.custom.report.timeline.nasemScoreTimelineChart.composedChart.referenceLines.y0.label.color,
              fontSize: 14,
              fontWeight: 400,
              textAnchor: 'end',
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default NasemScoreTimelineChart;
